import { useRef, useCallback, useState } from 'react';
import {
  PhotoUpload,
  MAX_PHOTO_WIDTH,
  MAX_PHOTO_HEIGHT,
  PHOTO_QUALITY,
  MAX_PHOTOS,
} from './modelSignup.types';

interface PhotoUploaderProps {
  photos: PhotoUpload[];
  onChange: (photos: PhotoUpload[]) => void;
  maxPhotos?: number;
  minLongEdge?: number;
  minShortEdge?: number;
  emptyLead?: string;
  emptySub?: string;
  addLabel?: string;
}

export function calculateResizedDimensions(
  originalWidth: number,
  originalHeight: number,
  options: { minLongEdge?: number; minShortEdge?: number } = {}
): { width: number; height: number } {
  let ratio = Math.min(MAX_PHOTO_WIDTH / originalWidth, MAX_PHOTO_HEIGHT / originalHeight, 1);

  if (options.minLongEdge || options.minShortEdge) {
    const longEdge = Math.max(originalWidth, originalHeight);
    const shortEdge = Math.min(originalWidth, originalHeight);
    const minimumRatio = Math.max(
      options.minLongEdge ? options.minLongEdge / longEdge : 0,
      options.minShortEdge ? options.minShortEdge / shortEdge : 0
    );
    ratio = Math.min(1, Math.max(ratio, minimumRatio));
  }

  return {
    width: Math.round(originalWidth * ratio),
    height: Math.round(originalHeight * ratio),
  };
}

// Resize an image to fit within max dimensions while maintaining aspect ratio
async function resizeImage(
  file: File,
  options: { minLongEdge?: number; minShortEdge?: number } = {}
): Promise<{ blob: Blob; preview: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const objectUrl = URL.createObjectURL(file);

    if (!ctx) {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      const originalWidth = img.naturalWidth || img.width;
      const originalHeight = img.naturalHeight || img.height;
      const longEdge = Math.max(originalWidth, originalHeight);
      const shortEdge = Math.min(originalWidth, originalHeight);

      if (
        (options.minLongEdge && longEdge < options.minLongEdge) ||
        (options.minShortEdge && shortEdge < options.minShortEdge)
      ) {
        URL.revokeObjectURL(objectUrl);
        reject(
          new Error(
            `${file.name || 'Selected photo'} is ${originalWidth} x ${originalHeight}px. Use a photo where the shorter side is at least ${options.minShortEdge}px and the longer side is at least ${options.minLongEdge}px.`
          )
        );
        return;
      }

      const { width, height } = calculateResizedDimensions(originalWidth, originalHeight, options);

      canvas.width = width;
      canvas.height = height;

      // Use better image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (blob) {
            resolve({
              blob,
              preview: canvas.toDataURL('image/jpeg', PHOTO_QUALITY),
              width,
              height,
            });
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/jpeg',
        PHOTO_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 15);
}

export function PhotoUploader({
  photos,
  onChange,
  maxPhotos = MAX_PHOTOS,
  minLongEdge,
  minShortEdge,
  emptyLead,
  emptySub,
  addLabel = 'Add photo',
}: PhotoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const processFiles = useCallback(
    async (files: File[]) => {
      const remainingSlots = maxPhotos - photos.length;
      const filesToProcess = files.slice(0, remainingSlots);
      const newPhotos: PhotoUpload[] = [];
      const rejected: string[] = [];

      for (let index = 0; index < filesToProcess.length; index++) {
        const file = filesToProcess[index];
        try {
          const { blob, preview, width, height } = await resizeImage(file, { minLongEdge, minShortEdge });
          newPhotos.push({
            id: generateId(),
            file,
            preview,
            resizedBlob: blob,
            width,
            height,
          });
        } catch (error) {
          console.error('Error processing image:', error);
          const slot = photos.length + index + 1;
          const reason = error instanceof Error ? error.message : `${file.name || 'Selected photo'} could not be processed.`;
          rejected.push(`Photo ${slot}: ${reason}`);
        }
      }

      if (newPhotos.length > 0) {
        onChange([...photos, ...newPhotos]);
      }

      if (rejected.length > 0) {
        const visibleRejected = rejected.slice(0, 3);
        const remainingRejected = rejected.length - visibleRejected.length;
        setUploadError(
          [
            `${rejected.length} image${rejected.length === 1 ? '' : 's'} could not be added.`,
            ...visibleRejected,
            remainingRejected > 0 ? `${remainingRejected} more image${remainingRejected === 1 ? '' : 's'} also failed.` : null,
          ].filter(Boolean).join(' ')
        );
      } else {
        setUploadError(null);
      }
    },
    [photos, onChange, maxPhotos, minLongEdge, minShortEdge]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      await processFiles(files);

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFiles]
  );

  const handleRemove = useCallback(
    (id: string) => {
      onChange(photos.filter((p) => p.id !== id));
    },
    [photos, onChange]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      e.dataTransfer.setData('text/plain', index.toString());
      e.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

      if (dragIndex === dropIndex) return;

      const newPhotos = [...photos];
      const [draggedPhoto] = newPhotos.splice(dragIndex, 1);
      newPhotos.splice(dropIndex, 0, draggedPhoto);
      onChange(newPhotos);
    },
    [photos, onChange]
  );

  // Handle drag events on the uploader container
  const handleContainerDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleContainerDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleContainerDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      if (files.length === 0) return;

      await processFiles(files);
    },
    [processFiles]
  );

  const canAddMore = photos.length < maxPhotos;

  // Empty state
  if (photos.length === 0) {
    return (
      <div
        className={`form-uploader ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleContainerDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleContainerDragLeave}
        onDrop={handleContainerDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
        />
        <div className="form-uploader-empty">
          <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3.5" y="4" width="17" height="16" rx="1.5"/>
            <circle cx="9" cy="10" r="1.6"/>
            <path d="M3.5 17l5-5 4 4 3-3 5 4"/>
          </svg>
          <p className="lead">{emptyLead ?? <>Drop images here, or <em>choose from your library</em>.</>}</p>
          <p className="sub">
            {emptySub ?? `Up to ${maxPhotos} photos. JPG, PNG, or HEIC.`}
          </p>
          <button
            type="button"
            className="pick"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose images
          </button>
          {uploadError && (
            <p className="sub" style={{ color: '#c53030', fontStyle: 'normal' }}>{uploadError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Photo grid */}
      <div className="grid grid-cols-3 gap-3">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            className="relative aspect-[3/4] rounded overflow-hidden border border-line bg-panel group cursor-move"
          >
            <img
              src={photo.preview}
              alt={`Upload ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {/* Primary badge for first photo */}
            {index === 0 && (
              <div className="absolute top-2 left-2 px-2 py-1 bg-text text-bg text-[9px] font-mediumr uppercase rounded">
                Primary
              </div>
            )}
            {/* Remove button */}
            <button
              onClick={() => handleRemove(photo.id)}
              className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity border border-white/20"
            >
              <span style={{ fontSize: '14px', lineHeight: 1 }}>×</span>
            </button>
            {/* Drag handle indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-black/50 rounded text-[9px] text-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
              Drag to reorder
            </div>
          </div>
        ))}

        {/* Add more button */}
        {canAddMore && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleContainerDragEnter}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={handleContainerDragLeave}
            onDrop={handleContainerDrop}
            className={`aspect-[3/4] rounded border border-dashed ${isDragging ? 'border-text bg-bg-secondary' : 'border-line hover:border-text-dim'} flex flex-col items-center justify-center gap-2 text-text-dim hover:text-text transition-colors`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className="text-[12px] italic">{addLabel}</span>
            <span className="text-[10px] text-text-faint">
              {photos.length} / {maxPhotos}
            </span>
          </button>
        )}
      </div>

      {/* Footer note */}
      <p className="text-[12px] text-text-dim italic">
        {photos.length} of {maxPhotos} added · drop more, or choose from your library.
      </p>
      {uploadError && (
        <p className="text-[12px] text-warn">
          {uploadError}
        </p>
      )}
    </div>
  );
}
